import { DateRange, DateRangeID, Flags } from '@fullstory/client-api';
import { intl } from '@fullstory/intl';
import {
  Computation,
  Measure,
  Metric,
  NumericAggregationMethod,
  UniqueAggregation,
} from '@fullstory/proto/fs/api/metrics';
import { Computation as FunnelComputation } from '@fullstory/proto/fs/api/metrics/funnelmetrics';
import { EventSearch, Scope } from '@fullstory/proto/fs/api/search';
import {
  Behavior,
  Constraints,
  Event,
  EventSequence,
  Platform,
  SegmentDefinition,
  TimeInterval,
  TimeRange,
} from '@fullstory/proto/fs/api/segment';
import {
  AnalysisMode,
  ComputeDimensionsRequest,
  ComputeFunnelMetricsRequest,
  ConversionParams,
  CoreWebVitalType,
  GenerateOpportunitiesRequest,
  GenerateSignalsRequest,
  ImpactScope,
  ImpactType,
  Opportunity,
  QueryCacheOptions,
  RequestAnalyticsAttributes,
  RequestAnalyticsIdType,
} from '@fullstory/proto/fs/services/alexa';
import {
  DefinedEvent,
  DefinedEventRecord,
  Funnel,
} from '@fullstory/proto/fs/services/search-settings';
import { DisplayCurrencyType } from '@fullstory/proto/fs/services/webber/frontend/display';
import { RevenueEvent } from '@fullstory/proto/fs/services/webber/conversions';
import {
  CreateOpportunitySnapshotRequest,
  ImpactsTableConfig,
} from '@fullstory/proto/fs/services/webber/internal/impacts';
import { Duration, int64, Timestamp } from '@fullstory/proto/google/protobuf';
import { EventType } from '@fullstory/search-types';
import { assertExhaustive, isNonNull } from '@fullstory/typeutils';
import { getRequestAnalytics } from '@fullstory/util/alexa-request-analytics';
import { isMidnight, resetDateToMidnight } from '@fullstory/util/date';
import { hashObject } from '@fullstory/util/hash';
import { isEmpty, isEqual, merge, some, sum, xor } from 'lodash';
import moment from 'moment';
import { defineMessage } from 'react-intl';
import { createSelector } from 'reselect';
import { AppState, RequestState } from '../../index';
import { allSpecObjectsBySlug } from '../specs';
import { getCustomApiEventsMap } from '../searchMeta';
import { updateSegmentTimeRange } from '../../utils';
import { getOrgId } from '../fullstory/selectors';
import {
  analysisModeForFunnel,
  appCacheRefresh,
  getConversionPctFromFunnelMetrics,
  getEventTextByStepIndex,
  getSavedFunnelsById,
  isRevenueFunnel,
  MEDIAN_PERCENTILE,
} from '../funnels';
import { LegacyMetricsRequestBody, metricsCacheOptionsRefresh } from '../metrics';
import { getDefinedEventsById } from '../searchMeta/definedEvents/selectors';
import { getSegmentsById } from '../segments/selectors';
import {
  ClickTypeFilter,
  ConversionSummaryData,
  ConversionSummaryDataV2,
  DateRanges,
  Datum,
  DimensionTypes,
  ImpactTypeFilter,
  OpportunitiesLoadingStatus,
  OpportunitiesWithID,
  ParsedImpactsTableConfig,
  SortProperty,
  Step,
  TodaySearchValue,
} from './types';
import {
  adjustSinceDateIfNeeded,
  dateRangeToTimeRange,
  filterOpportunitiesBySignalCandidates,
  opportunityFromSnapshot,
  validateAndAdjustConfig,
} from './util';
import { FunnelDetail, SegmentDetail } from '@fullstory/proto/fs/services/dx-objects';
import { getIsReady } from '@fullstory/redux-util';
import { getNamedElementsById } from '../namedElements/selectors';
import { pagesById } from '../pages/selectors';

// TODO (jb): Get off the deprecated table component.
// This enum is a copy from the DeprecatedTable component
enum DeprecatedSortOrder {
  Ascending = 'asc',
  Descending = 'desc',
}

export const getImpactsPendingConfig = (state: AppState) => state.impacts.pendingConfig;

export const getAffectedConversion = (opportunity: Opportunity): number => {
  const toIndex = getOpportunityToIndex(opportunity);
  const summary = conversionSummaryFromOpportunity(opportunity);
  return summary[toIndex]?.affectedConversion || 0;
};

export const getUnaffectedConversion = (opportunity: Opportunity): number => {
  const toIndex = getOpportunityToIndex(opportunity);
  const summary = conversionSummaryFromOpportunity(opportunity);
  return summary[toIndex]?.unaffectedConversion || 0;
};

export const getAffectedUserCount = (opportunity: Opportunity): number => {
  const fromIndex = getOpportunityFromIndex(opportunity);
  const summary = conversionSummaryFromOpportunity(opportunity);
  return summary[fromIndex]?.affected || 0;
};

export const numConfiguredInputs = (state: AppState): number => {
  if (!state.impacts) return 0;
  return (
    state.impacts.currentConfig.customEvents.length +
    state.impacts.currentConfig.watchedElements.length +
    state.impacts.currentConfig.definedEventIds.length +
    state.impacts.currentConfig.pageviewEvents.length
  );
};

export const numPendingConfiguredInputs = (state: AppState): number => {
  if (!state.impacts) return 0;
  return (
    state.impacts.pendingConfig.customEvents.length +
    state.impacts.pendingConfig.watchedElements.length +
    state.impacts.pendingConfig.definedEventIds.length +
    state.impacts.pendingConfig.pageviewEvents.length
  );
};

export const numSessionsOrUsers = (state: AppState): number => {
  if (!state.impacts) return 0;
  return state.impacts.funnelMetrics
    ? state.impacts.funnelMetrics.data?.data?.steps[0]?.table[0]?.value || 0
    : 0;
};

const webVitalFilters: Partial<keyof ImpactTypeFilter>[] = [
  ImpactType.IMPACT_TYPE_FID,
  ImpactType.IMPACT_TYPE_LCP,
  ImpactType.IMPACT_TYPE_CLS,
  ImpactType.IMPACT_TYPE_TBT,
  ImpactType.IMPACT_TYPE_INP,
  ImpactType.IMPACT_TYPE_TTI,
  ImpactType.IMPACT_TYPE_TTFB,
];

export const numCannedFilters = (state: AppState): number => {
  if (!state.impacts) return 0;
  const numClickFilters = Object.values(state.impacts.currentConfig.clickTypeFilter).filter(
    Boolean,
  ).length;
  let webVitalCount = 0;
  if (state.impacts.currentConfig.isSplittingWebVitals) {
    for (let i = 0; i < webVitalFilters.length; i++) {
      if (state.impacts.currentConfig.impactTypeFilter[webVitalFilters[i]!]) {
        webVitalCount++;
      }
    }
  } else {
    webVitalCount = state.impacts.currentConfig.impactTypeFilter[
      ImpactType.IMPACT_TYPE_CORE_WEB_VITALS
    ]
      ? 1
      : 0;
  }

  return (
    numClickFilters +
    (state.impacts.currentConfig.impactTypeFilter[ImpactType.IMPACT_TYPE_PERF_EVENT] ? 1 : 0) +
    (state.impacts.currentConfig.impactTypeFilter[ImpactType.IMPACT_TYPE_AJAX_ERROR] ? 1 : 0) +
    webVitalCount
  );
};

export const numPendingCannedFilters = (state: AppState): number => {
  if (!state.impacts) return 0;
  const numClickFilters = Object.values(state.impacts.pendingConfig.clickTypeFilter).filter(
    Boolean,
  ).length;
  let webVitalCount = 0;
  if (state.impacts.currentConfig.isSplittingWebVitals) {
    for (let i = 0; i < webVitalFilters.length; i++) {
      if (state.impacts.pendingConfig.impactTypeFilter[webVitalFilters[i]!]) {
        webVitalCount++;
      }
    }
  } else {
    webVitalCount = state.impacts.pendingConfig.impactTypeFilter[
      ImpactType.IMPACT_TYPE_CORE_WEB_VITALS
    ]
      ? 1
      : 0;
  }
  return (
    numClickFilters +
    (state.impacts.pendingConfig.impactTypeFilter[ImpactType.IMPACT_TYPE_PERF_EVENT] ? 1 : 0) +
    (state.impacts.pendingConfig.impactTypeFilter[ImpactType.IMPACT_TYPE_AJAX_ERROR] ? 1 : 0) +
    webVitalCount
  );
};

export const getPendingImpactTableConfig = (
  impacts: AppState['impacts'],
): Partial<ImpactsTableConfig> => {
  const currentConfig = impacts.currentConfigId
    ? impacts.configsById[impacts.currentConfigId as string]
    : null;
  const impactTypeFilters = impacts.currentConfig.impactTypeFilter;
  const clickTypeFilters = impacts.currentConfig.clickTypeFilter;
  return {
    id: impacts.currentConfigId || undefined,
    name: currentConfig?.name || undefined,
    starred: currentConfig ? currentConfig.starred : false,
    funnelId: impacts.currentConfig.funnelId || undefined,
    segmentId: impacts.currentConfig.segmentId,
    customEvents: impacts.currentConfig.customEvents,
    watchedElements: impacts.currentConfig.watchedElements,
    customQueries: impacts.currentConfig.customQueries,
    startTime: Timestamp.fromDate(impacts.currentConfig.startDate),
    endTime: Timestamp.fromDate(impacts.currentConfig.endDate),
    dateRangeId: impacts.currentConfig.cannedDateRangeId,
    impactTypeFilters: Object.keys(impactTypeFilters)
      .filter(key => impactTypeFilters[key as unknown as keyof ImpactTypeFilter])
      .map(key => +key),
    clickTypeFilters: Object.keys(clickTypeFilters)
      .filter(key => clickTypeFilters[key as unknown as keyof ClickTypeFilter])
      .map(key => +key),
    definedEventIds: impacts.currentConfig.definedEventIds,
    pageviewEvents: impacts.currentConfig.pageviewEvents,
  };
};

const getPartialConfig = (
  config: Partial<ImpactsTableConfig>,
): Partial<ImpactsTableConfig> & { startDate: Date | null; endDate: Date | null } => ({
  funnelId: config.funnelId,
  segmentId: config.segmentId,
  startDate: Timestamp.toDate(config.startTime),
  endDate: Timestamp.toDate(config.endTime),
  dateRangeId: config.dateRangeId,
  customEvents: config.customEvents,
  watchedElements: config.watchedElements,
  customQueries: config.customQueries,
  impactTypeFilters: config.impactTypeFilters,
  clickTypeFilters: config.clickTypeFilters,
  definedEventIds: config.definedEventIds,
  pageviewEvents: config.pageviewEvents,
});

export const impactTableConfigHasUnsavedChanges = (state: AppState): boolean => {
  const { currentConfigId } = state.impacts;
  if (!currentConfigId) {
    return false;
  }
  const currentConfig = state.impacts.configsById[currentConfigId as string];
  if (!currentConfig) return false;
  const partialPending = getPartialConfig(getPendingImpactTableConfig(state.impacts));
  const partialCurrent = getPartialConfig(currentConfig);

  return !isEqual(partialPending, partialCurrent);
};

// We have to add 1 day to the to date to make the query inclusive of it
function addOneDay(date: Date): Date {
  const outDate = new Date(date);
  outDate.setDate(outDate.getDate() + 1);
  return outDate;
}

export const getEndDate = (state: AppState) => state.impacts.currentConfig.endDate;
export const getStartDate = (state: AppState) => state.impacts.currentConfig.startDate;
const getCannedDateRangeID = (state: AppState) => state.impacts.currentConfig.cannedDateRangeId;
export const getCannedDateRange = createSelector(
  getCannedDateRangeID,
  getStartDate,
  getEndDate,
  (dateRangeId, startDate, endDate): DateRange | null => {
    const dateRange = DateRanges.find(d => d.id === dateRangeId);
    if (dateRange?.id === DateRangeID.Custom && startDate && endDate) {
      return {
        ...dateRange,
        range: {
          Start: resetDateToMidnight(startDate).toISOString(),
          End: resetDateToMidnight(endDate).toISOString(),
        },
      };
    }
    if (dateRange?.id === DateRangeID.Since) {
      return {
        ...dateRange,
        range: {
          Start: adjustSinceDateIfNeeded(startDate).toISOString(),
          End: dateRange.range.End,
        },
      };
    }
    return dateRange || null;
  },
);

const getPendingEndDate = (state: AppState) => state.impacts.pendingConfig.endDate;
const getPendingStartDate = (state: AppState) => state.impacts.pendingConfig.startDate;
const getPendingCannedDateRangeID = (state: AppState) =>
  state.impacts.pendingConfig.cannedDateRangeId;
export const getPendingCannedDateRange = createSelector(
  getPendingCannedDateRangeID,
  getPendingStartDate,
  getPendingEndDate,
  (dateRangeId, startDate, endDate): DateRange | null => {
    let dateRange = DateRanges.find(d => d.id === dateRangeId);
    // Default dashboardOnly time ranges and custom time ranges longer than 31 days to past month (1000 * 60 * 60 * 24 = 86,400,000 ms/day)
    const msDay = 86400000;
    if (
      dateRange?.dashboardOnly ||
      (dateRange?.id === DateRangeID.Custom &&
        (endDate?.getTime() ?? 0) - (startDate?.getTime() ?? 0) > 31 * msDay)
    ) {
      dateRange = DateRanges.find(d => d.id === DateRangeID.PastMonth);
    }
    if (dateRange?.id === DateRangeID.Custom && startDate && endDate) {
      return {
        ...dateRange,
        range: {
          Start: resetDateToMidnight(startDate).toISOString(),
          End: resetDateToMidnight(endDate).toISOString(),
        },
      };
    }
    if (dateRange?.id === DateRangeID.Since && startDate) {
      return {
        ...dateRange,
        range: {
          Start: resetDateToMidnight(startDate).toISOString(),
          End: dateRange.range.End,
        },
      };
    }
    return dateRange || null;
  },
);

export const getDaysInDateRange = createSelector(
  getCannedDateRange,
  getStartDate,
  getEndDate,
  (dateRange, startDate, endDate): number => {
    let adjustedEndDate;

    switch (dateRange?.id) {
      case DateRangeID.Today:
        return 1;
      case DateRangeID.Yesterday:
        return 1;
      case DateRangeID.Past60Minutes:
        return 1.0 / 24.0;
      case DateRangeID.Past3Hours:
        return 3.0 / 24.0;
      case DateRangeID.Past6Hours:
        return 6.0 / 24.0;
      case DateRangeID.Past24Hours:
        return 1;
      case DateRangeID.PastWeek:
        return 7;
      case DateRangeID.PastMonth:
        return 30;
      case DateRangeID.Custom:
        adjustedEndDate = moment(endDate);
        if (isMidnight(adjustedEndDate)) {
          // We need to count the entire endDate by adding 1 since we actually perform analysis from start of startDate to start of the endDate + 1
          adjustedEndDate = adjustedEndDate.add(1, 'day');
        }

        return (endDate && startDate && adjustedEndDate.diff(moment(startDate), 'days')) || 1;
      case DateRangeID.Since:
        // Count the entirely of today by going to start of tomorrow
        adjustedEndDate = moment(moment.now()).add(1, 'day').startOf('day');

        return (startDate && adjustedEndDate.diff(moment(startDate), 'days')) || 1;
    }

    return 0;
  },
);

export const getAnnualMultiplier = createSelector(getDaysInDateRange, (daysInDateRange): number => {
  let multiplier = 0;
  if (daysInDateRange) {
    multiplier = 365 / daysInDateRange;
  }

  return multiplier;
});

// Returns the SearchV2 segment.TimeRange from the DatePicker results
export const getAnalysisTimeRange = createSelector(getCannedDateRange, daterange =>
  daterange ? dateRangeToTimeRange(daterange) : null,
);

const getCurrentSegmentId = (state: AppState) => state.impacts.currentConfig.segmentId;
const getPendingSegmentId = (state: AppState) => state.impacts.pendingConfig.segmentId;
export const getCurrentSegment = createSelector(
  getCurrentSegmentId,
  getSegmentsById,
  (segmentId, segmentsById): SegmentDetail | null => segmentsById[segmentId] ?? null,
);

export const getPendingSegment = createSelector(
  getPendingSegmentId,
  getSegmentsById,
  (segmentId, segmentsById): SegmentDetail | null => segmentsById[segmentId] ?? null,
);

export const getRevenueEvent = (state: AppState): RevenueEvent | null =>
  state.impacts.revenueAttribution.revenueEvent.data;
export const getRevenueEventRequestState = (state: AppState): RequestState<RevenueEvent> =>
  state.impacts.revenueAttribution.revenueEvent;
export const getRevenueEventReady = (state: AppState): boolean =>
  getIsReady(state.impacts.revenueAttribution.revenueEvent);
export const getRevenueDefinedEvent = createSelector(
  getRevenueEvent,
  getDefinedEventsById,
  (revenueEvent, definedEventsById): DefinedEventRecord | null => {
    const definedEventId = revenueEvent?.definedEventId;
    if (definedEventId) {
      return definedEventsById.get(definedEventId) || null;
    }
    return null;
  },
);
export const getRevenueEventCustomEventName = createSelector(
  getRevenueEvent,
  getRevenueDefinedEvent,
  (revenueEvent, revenueDefinedEvent): string => {
    if (!revenueEvent) return '';

    if (revenueDefinedEvent && revenueDefinedEvent.definedEvent?.events?.length == 1) {
      const event = revenueDefinedEvent.definedEvent.events[0];
      if (event?.primary?.custom?.named?.name) {
        return event.primary.custom.named.name;
      }
    }

    return revenueEvent.eventName ?? '';
  },
);

const getPendingConfig = (state: AppState) => state.impacts.pendingConfig;
const getCurrentConfig = (state: AppState) => state.impacts.currentConfig;

function isMatchingConfigs(
  currentConfig: ReturnType<typeof getCurrentConfig>,
  pendingConfig: ReturnType<typeof getPendingConfig>,
): boolean {
  const {
    impactTypeFilter: currentImpactTypeFilters,
    isSplittingWebVitals,
    ...restOfCurrent
  } = currentConfig;
  const { impactTypeFilter: pendingImpactTypeFilters, ...restOfPending } = pendingConfig;
  let impactFiltersMatch = true;
  const currentActiveFilters = Object.keys(currentImpactTypeFilters)
    .filter(k => currentImpactTypeFilters[k as unknown as keyof ImpactTypeFilter])
    .map(k => +k);
  const pendingActiveFilters = Object.keys(pendingImpactTypeFilters)
    .filter(k => pendingImpactTypeFilters[k as unknown as keyof ImpactTypeFilter])
    .map(k => +k);
  const xorOfImpactTypeFilters = xor(currentActiveFilters, pendingActiveFilters);

  if (isSplittingWebVitals) {
    impactFiltersMatch = xorOfImpactTypeFilters.length === 0;
  } else {
    impactFiltersMatch = !some(xorOfImpactTypeFilters, i => i < ImpactType.IMPACT_TYPE_FID);
  }

  const configObjectsMatch = isEqual(restOfPending, restOfCurrent);

  // if objects don't match or filters don't match, then there are unsaved changes
  return configObjectsMatch && impactFiltersMatch;
}

export const isValidPendingConfig = createSelector(
  getPendingConfig,
  getCurrentConfig,
  (pendingConfig, currentConfig) => !isMatchingConfigs(currentConfig, pendingConfig),
);

export const getCurrentFunnelId = (state: AppState) => state.impacts.currentConfig.funnelId;
const getPendingFunnelId = (state: AppState) => state.impacts.pendingConfig.funnelId;
const getCurrentConfigUsesUnnamedFunnel = (state: AppState) => state.impacts.currentConfig.unnamed;
export const getUnnamedFunnelsById = (state: AppState) => state.funnels.unnamedFunnels.data;
const getPendingConfigUsesUnnamedFunnel = (state: AppState) => state.impacts.pendingConfig.unnamed;

export const getCurrentFunnel = createSelector(
  getSavedFunnelsById,
  getCurrentFunnelId,
  getCurrentConfigUsesUnnamedFunnel,
  getUnnamedFunnelsById,
  (funnelsById, funnelId, unnamed, unnamedFunnelsById): Funnel | null => {
    if (!funnelId) return null;
    if (unnamed) {
      return unnamedFunnelsById?.[funnelId]?.funnel ?? null;
    }
    return funnelsById[funnelId]?.funnel?.funnel ?? null;
  },
);

const getCurrentParamsForSnapshot = createSelector(
  getCurrentSegment,
  getCurrentFunnel,
  getAnalysisTimeRange,
  (segment, funnel, analysisTimeRange): any => {
    if (!segment || !funnel) return undefined;

    const segmentDefinition: SegmentDefinition = updateSegmentTimeRange(
      { ...segment.segment!.segment!.segmentDefinition! },
      analysisTimeRange,
    );

    const eventSequence: EventSequence = merge({}, funnel.events, {
      constraints: { ...funnel?.events?.constraints, timeRange: analysisTimeRange },
    });

    return {
      segmentDefinition,
      eventSequence,
      name: funnel.name,
    };
  },
);

export const getPendingFunnel = createSelector(
  getSavedFunnelsById,
  getPendingFunnelId,
  getPendingConfigUsesUnnamedFunnel,
  getUnnamedFunnelsById,
  (funnelsById, funnelId, unnamed, unnamedFunnelsById): FunnelDetail | null => {
    if (!funnelId) return null;
    if (unnamed) {
      return {
        funnel: {
          funnel: unnamedFunnelsById?.[funnelId]?.funnel ?? null,
          metadata: null,
          isDcmCreated: false,
        },
        starred: false,
        lastViewed: null,
      };
    }
    return funnelsById[funnelId] ?? null;
  },
);

export const getDataForAnnualProjection = createSelector(
  getDaysInDateRange,
  getAnnualMultiplier,
  (daysInRange, multiplier) => ({ daysInRange, multiplier }),
);

export const getAnalysisMode = createSelector(getCurrentFunnel, funnel =>
  funnel ? analysisModeForFunnel(funnel.events) : AnalysisMode.ANALYSIS_MODE_INVALID,
);

const getInSameSession = createSelector(
  getAnalysisMode,
  analysisMode =>
    analysisMode === AnalysisMode.ANALYSIS_MODE_INDV_SAME_SESSION ||
    analysisMode === AnalysisMode.ANALYSIS_MODE_SESSIONS,
);

export const getIsSessionMode = createSelector(
  getAnalysisMode,
  analysisMode => analysisMode === AnalysisMode.ANALYSIS_MODE_SESSIONS,
);

export const getAnalysisSummary = createSelector(
  getCurrentSegment,
  getCannedDateRange,
  numCannedFilters,
  numConfiguredInputs,
  (currentSegment, dateRange, numCannedFilters, numConfiguredInputs) => ({
    cohort: currentSegment?.segment?.segment?.name ?? '',
    dateRange,
    numSignals: numCannedFilters + numConfiguredInputs,
  }),
);

/**
 * From the app state, get the ConversionParams, or return undefined if there's not enough data.
 */
export const getConversionParams = createSelector(
  getCurrentSegment,
  getCurrentFunnel,
  getAnalysisTimeRange,
  getAnalysisMode,
  getInSameSession,
  (segment, funnel, analysisTimeRange, analysisMode, inSameSession): ConversionParams | null => {
    if (!segment || !funnel) return null;

    const segmentDefinition: SegmentDefinition = updateSegmentTimeRange(
      { ...segment.segment!.segment!.segmentDefinition! },
      analysisTimeRange,
    );

    const eventSequence: EventSequence = merge({}, funnel.events, {
      constraints: { ...funnel?.events?.constraints, timeRange: analysisTimeRange },
    });

    return {
      analysisTimeRange,
      segmentDefinition,
      funnelEventSequence: eventSequence,
      requireSameSession: inSameSession,
      analysisMode,
    };
  },
);

/**
 * From the app state, get the Computation, or return undefined if there's not enough data.
 */
export const getFunnelMetricComputation = createSelector(
  getCurrentSegment,
  getCurrentFunnel,
  getAnalysisTimeRange,
  getAnalysisMode,
  (segment, funnel, analysisTimeRange, analysisMode): FunnelComputation | null => {
    if (!segment || !funnel) return null;

    const segmentDefinition: SegmentDefinition = updateSegmentTimeRange(
      { ...segment.segment!.segment!.segmentDefinition! },
      analysisTimeRange,
    );

    const aggregation =
      analysisMode === AnalysisMode.ANALYSIS_MODE_SESSIONS
        ? UniqueAggregation.UNIQUE_AGGREGATION_SESSION
        : UniqueAggregation.UNIQUE_AGGREGATION_INDIVIDUAL;
    const events: EventSequence = merge({}, funnel.events, {
      constraints: { ...funnel?.events?.constraints, timeRange: analysisTimeRange },
    });

    const computation: FunnelComputation = {
      funnel: {
        events,
        aggregation: {
          unique: aggregation,
        },
        dimensionApplication: null,
      },
      correlatedEvents: [],
      timeRange: analysisTimeRange,
      timeInterval: TimeInterval.TIME_INTERVAL_ENTIRE_RANGE,
      segmentDefinitions: [segmentDefinition],
      matchingBehaviors: [],
      dimensionSettings: null,
      timeToComplete: { percentiles: [MEDIAN_PERCENTILE] },
    };
    return computation;
  },
);

const getCacheOptions = createSelector(
  getCannedDateRange,
  (cannedDateRange): QueryCacheOptions | null => {
    if (
      cannedDateRange?.range.End === TodaySearchValue ||
      cannedDateRange?.range.End === resetDateToMidnight(new Date()).toISOString()
    ) {
      return {
        getIfAgeLessThan: appCacheRefresh.getIfAgeLessThan,
        computeIfOlderThan: appCacheRefresh.computeIfOlderThan,
        skipCache: false,
        queue: QueryCacheOptions.Queue.QUEUE_INVALID,
      };
    }
    return null;
  },
);

const getCurrentCustomEvents = createSelector(
  getCurrentConfig,
  getCustomApiEventsMap,
  allSpecObjectsBySlug,
  (currentConfig, customApiEventsMap, specObjectsBySlug) =>
    currentConfig.customEvents.filter(customEvent => {
      if (customEvent.name) {
        return !customApiEventsMap.get(customEvent.name)?.archived;
      } else if (customEvent.slug) {
        return specObjectsBySlug[customEvent.slug]?.active;
      }
      return false;
    }),
);

const getCurrentWatchedElements = createSelector(
  getCurrentConfig,
  getNamedElementsById,
  allSpecObjectsBySlug,
  (currentConfig, namedElementsById, specObjectsBySlug) =>
    currentConfig.watchedElements.filter(watchedElement => {
      if (watchedElement.targetNamedElementId) {
        return (
          namedElementsById.has(watchedElement.targetNamedElementId) &&
          !namedElementsById.get(watchedElement.targetNamedElementId)?.metadata?.archived
        );
      } else if (watchedElement.targetSlug) {
        return specObjectsBySlug[watchedElement.targetSlug]?.active;
      }
      return false;
    }),
);
const getCurrentDefinedEventIds = createSelector(
  getCurrentConfig,
  getDefinedEventsById,
  (currentConfig, definedEventsById) =>
    currentConfig.definedEventIds.filter(
      id => definedEventsById.has(id) && !definedEventsById.get(id)?.metadata?.archived,
    ),
);
const getCurrentPageViewEvents = createSelector(
  getCurrentConfig,
  pagesById,
  allSpecObjectsBySlug,
  (currentConfig, pages, specObjectsBySlug) =>
    currentConfig.pageviewEvents.filter(pageviewEvent => {
      if (pageviewEvent.pageDefId) {
        return (
          pages.has(int64.toNumber(pageviewEvent.pageDefId)) &&
          !pages.get(int64.toNumber(pageviewEvent.pageDefId))?.metadata?.archived
        );
      } else if (pageviewEvent.specSlug) {
        return specObjectsBySlug[pageviewEvent.specSlug]?.active;
      }
      return false;
    }),
);
const getImpactTypeFilter = (state: AppState) => state.impacts.currentConfig.impactTypeFilter;
const getClickTypeFilter = (state: AppState) => state.impacts.currentConfig.clickTypeFilter;
const getWebVitalsFilter = (state: AppState): { [key: number]: boolean } => ({
  [CoreWebVitalType.CORE_WEB_VITAL_TYPE_FIRST_INPUT_DELAY]:
    !!state.impacts.currentConfig.impactTypeFilter[ImpactType.IMPACT_TYPE_FID],
  [CoreWebVitalType.CORE_WEB_VITAL_TYPE_LARGEST_CONTENTFUL_PAINT]:
    !!state.impacts.currentConfig.impactTypeFilter[ImpactType.IMPACT_TYPE_LCP],
  [CoreWebVitalType.CORE_WEB_VITAL_TYPE_CUMULATIVE_LAYOUT_SHIFT]:
    !!state.impacts.currentConfig.impactTypeFilter[ImpactType.IMPACT_TYPE_CLS],
  [CoreWebVitalType.WEB_VITAL_TYPE_TOTAL_BLOCKING_TIME]:
    !!state.impacts.currentConfig.impactTypeFilter[ImpactType.IMPACT_TYPE_TBT],
  [CoreWebVitalType.WEB_VITAL_TYPE_INTERACTION_TO_NEXT_PAINT]:
    !!state.impacts.currentConfig.impactTypeFilter[ImpactType.IMPACT_TYPE_INP],
  [CoreWebVitalType.WEB_VITAL_TYPE_TIME_TO_INTERACTIVE]:
    !!state.impacts.currentConfig.impactTypeFilter[ImpactType.IMPACT_TYPE_TTI],
  [CoreWebVitalType.WEB_VITAL_TYPE_TIME_TO_FIRST_BYTE]:
    !!state.impacts.currentConfig.impactTypeFilter[ImpactType.IMPACT_TYPE_TTFB],
});

export const getSignalParams = createSelector(
  getOrgId,
  getCurrentCustomEvents,
  getCurrentWatchedElements,
  getConversionParams,
  getImpactTypeFilter,
  getClickTypeFilter,
  getCacheOptions,
  getCurrentDefinedEventIds,
  getDefinedEventsById,
  getCurrentPageViewEvents,
  getWebVitalsFilter,
  (
    orgId,
    customEvents,
    watchedElements,
    conversionParams,
    impactTypeFilter,
    clickTypeFilter,
    cacheOptions,
    currentDefinedEventIds,
    definedEventsByIds,
    pageViewEvents,
    webVitalsFilter,
  ): Omit<GenerateSignalsRequest, 'timezone' | 'now'> | null => {
    if (!conversionParams) return null;
    const definedEvents: DefinedEvent[] = [];
    currentDefinedEventIds.forEach(id => {
      const definedEventById = definedEventsByIds.get(id);
      if (definedEventById?.definedEvent) {
        definedEvents.push(definedEventById.definedEvent);
      }
    });

    return {
      conversionParams,
      customEvents,
      seenEvents: watchedElements,
      definedEvents,
      orgId,
      badClicks: Object.keys(clickTypeFilter)
        .filter(k => clickTypeFilter[k as unknown as keyof ClickTypeFilter])
        .map(k => int64.toNumber(k as int64)),
      // TODO(jb): Update thresholds to 934/1600. Also update to be server side?
      performanceThresholds: impactTypeFilter[ImpactType.IMPACT_TYPE_PERF_EVENT]
        ? [int64.fromNumber(3000), int64.fromNumber(6000)]
        : [],
      ajaxErrors: impactTypeFilter[ImpactType.IMPACT_TYPE_AJAX_ERROR] ? true : false,
      cacheOptions,
      coreWebVitals: impactTypeFilter[ImpactType.IMPACT_TYPE_CORE_WEB_VITALS] ? true : false,
      pageViews: pageViewEvents,
      webVitals: Object.keys(webVitalsFilter)
        .filter(k => webVitalsFilter[k as unknown as number])
        .map(k => int64.toNumber(k as int64)),
    };
  },
);

export const getCurrentConfigUsesCustomSignals = (state: AppState) => {
  const config = state.impacts.currentConfig;

  if (config.customEvents.length) return true;
  if (config.definedEventIds.length) return true;
  if (config.watchedElements.length) return true;
  if (config.pageviewEvents.length) return true;

  return false;
};

export const isValidPendingFunnel = createSelector(
  getCurrentFunnel,
  getPendingFunnel,
  (currentFunnel, pendingFunnel) => {
    const currentEventSequence = currentFunnel?.events;
    const pendingEventSequence = pendingFunnel?.funnel?.funnel?.events;
    if (!currentEventSequence || !pendingEventSequence) return false;
    const currentConstraints = currentEventSequence.constraints;
    const pendingConstraints = pendingEventSequence.constraints;
    return (
      !isEqual(currentEventSequence, pendingEventSequence) ||
      currentConstraints?.inSameSession !== pendingConstraints?.inSameSession ||
      currentConstraints?.withinTime !== pendingConstraints?.withinTime
    );
  },
);

export const getFunnelMetricsRequest = createSelector(
  getOrgId,
  getFunnelMetricComputation,
  getCacheOptions,
  getCurrentFunnelId,
  (
    orgId,
    computation,
    cacheOptions,
    funnelId,
  ): Omit<ComputeFunnelMetricsRequest, 'timezone' | 'now'> | null => {
    if (!computation) return null;
    return {
      computation,
      orgId,
      cacheOptions,
      sampleOptions: null,
      requestAnalytics:
        getRequestAnalytics(RequestAnalyticsIdType.FUNNEL, !!funnelId, funnelId ?? undefined) ??
        null,
    };
  },
);

const getFunnelMetricsData = (state: AppState) => state.impacts.funnelMetrics?.data;
export const getFunnelMetricsSteps = createSelector(
  getFunnelMetricsData,
  (funnelMetrics): Step[] => {
    const steps = funnelMetrics?.data?.steps;
    if (!steps) return [];
    return steps.map((d, i) => {
      const total = steps[i - 1]?.table[0];
      return {
        total: total ? total.value : 0,
        value: d.table[0]?.value ?? 0,
      };
    });
  },
);

export const hasErrorFunnelData = (state: AppState) => state.impacts.funnelMetrics?.error;
export const getFunnelSafeMessage = (state: AppState) => state.impacts.funnelMetrics?.safeMessage;

const getGeneratedSignalsError = (state: AppState) => state.impacts.generatedSignals.error;
const getGeneratedSignalsLoading = (state: AppState) => state.impacts.generatedSignals.loading;
export const getGeneratedSignalsById = (state: AppState) => state.impacts.generatedSignals.byId;
export const getOpportunityBatchVersion = (state: AppState) =>
  state.impacts.opportunityBatchVersion;
export const getMutedOpportunityConfigsBySignalKey = (state: AppState) =>
  state.impacts.mutedOpportunityConfigs.bySignalKey;

export const getGeneratedSignals = createSelector(getGeneratedSignalsById, signalsById =>
  Object.values(signalsById),
);

export const getGeneratedOpportunitiesById = (state: AppState) =>
  state.impacts.generatedOpportunities.opportunitiesById;

export const getOpportunityDetailId = (state: AppState) => state.impacts.opportunityDetail;

export const getOpportunityDetail = createSelector(
  getOpportunityDetailId,
  getGeneratedOpportunitiesById,
  (id, byId) => byId[id],
);

export const getOpportunityDetailIsMuted = createSelector(
  getOpportunityDetail,
  getMutedOpportunityConfigsBySignalKey,
  (detailOpportunity, mutedOpps) => {
    const signalKey = detailOpportunity?.signal?.signalKey;
    return signalKey && signalKey in mutedOpps;
  },
);

export const getDimensionsRequest = (appState: AppState) =>
  createSelector(
    getOrgId,
    getConversionParams,
    getOpportunityDetail,
    getCacheOptions,
    (
      orgId,
      conversionParams,
      opportunityDetail,
      cacheOptions,
    ): Omit<ComputeDimensionsRequest, 'now' | 'timezone'> | null => {
      if (!conversionParams) return null;
      return {
        orgId,
        conversionParams,
        signal: opportunityDetail?.signal ?? null,
        cacheOptions,
        impactStep: int64.fromNumber(opportunityDetail?.impactStats?.fromIndex || 0),
        dimensionTypes: DimensionTypes,
      };
    },
  )(appState);

export const getOpportunityParams = createSelector(
  getOrgId,
  getConversionParams,
  getGeneratedSignalsError,
  getGeneratedSignalsLoading,
  getCacheOptions,
  (
    orgId,
    conversionParams,
    signalError,
    signalLoading,
    cacheOptions,
  ): Omit<GenerateOpportunitiesRequest, 'signals' | 'now' | 'timezone'> | null => {
    if (!conversionParams || signalError || signalLoading) return null;
    return {
      orgId,
      conversionParams,
      cacheOptions,
    };
  },
);

const getGeneratedOpportunitiesListIds = (state: AppState) =>
  state.impacts.generatedOpportunities.opportunityList;
const getGeneratedOpportunitiesList = createSelector(
  getGeneratedOpportunitiesListIds,
  getGeneratedOpportunitiesById,
  (list, opportunities): OpportunitiesWithID[] => list.map(d => opportunities[d]).filter(isNonNull),
);

type SortOpportunitiesFunction = (a: OpportunitiesWithID, b: OpportunitiesWithID) => number;

function sortOpportunities(
  opportunities: OpportunitiesWithID[],
  sortProperty: SortProperty,
  sortOrder: DeprecatedSortOrder,
) {
  const direction = sortOrder === DeprecatedSortOrder.Ascending ? -1 : 1;
  let sortFunction: SortOpportunitiesFunction;
  switch (sortProperty) {
    case SortProperty.affectedUsers:
      sortFunction = (a: OpportunitiesWithID, b: OpportunitiesWithID): number =>
        direction * (getAffectedUsers(b) - getAffectedUsers(a));
      break;
    case SortProperty.conversionDelta:
      sortFunction = (a: OpportunitiesWithID, b: OpportunitiesWithID): number =>
        direction * ((b.impactStats?.conversionDelta || 0) - (a.impactStats?.conversionDelta || 0));
      break;
    case SortProperty.conversionsLost:
      sortFunction = (a, b): number => {
        let result = resultDiff(b.impactStats?.conversionsLost, a.impactStats?.conversionsLost);
        if (result === 0)
          result = (b.impactStats?.confidence || 0) - (a.impactStats?.confidence || 0);
        return direction * result;
      };
      break;
    case SortProperty.annualizedConversionsLost:
      sortFunction = (a, b): number => {
        let result = resultDiff(b.impactStats?.conversionsLost, a.impactStats?.conversionsLost);
        if (result === 0)
          result = (b.impactStats?.confidence || 0) - (a.impactStats?.confidence || 0);
        return direction * result;
      };
      break;
    case SortProperty.confidenceLevel:
      sortFunction = (a, b): number => {
        let result = (b.impactStats?.confidence || 0) - (a.impactStats?.confidence || 0);
        if (result === 0) {
          result = resultDiff(b.impactStats?.conversionsLost, a.impactStats?.conversionsLost);
        }
        return direction * result;
      };
      break;
    case SortProperty.annualizedValue: // average order value applies to the whole funnel, so sorting is basically the same as conversions lost
      sortFunction = (a, b): number => {
        let result = resultDiff(b.impactStats?.conversionsLost, a.impactStats?.conversionsLost);
        if (result === 0)
          result = (b.impactStats?.confidence || 0) - (a.impactStats?.confidence || 0);
        return direction * result;
      };
      break;
    default:
      assertExhaustive(sortProperty);
  }
  return opportunities.slice().sort(sortFunction);
}

function resultDiff(b?: int64, a?: int64): number {
  const bn = b ? int64.toNumber(b) : -1;
  const an = a ? int64.toNumber(a) : -1;
  return bn - an;
}

// filter out opportunities that have 0 confidence and 0 conversions lost
// also filter out opportunities that are not part of the analysis configuration
// e.g. if they came from custom signal candidates
const getNonZeroOpportunities = createSelector(
  getGeneratedOpportunitiesList,
  getCurrentConfig,
  (generatedOpportunities, currentConfig) =>
    filterOpportunitiesBySignalCandidates(generatedOpportunities, currentConfig),
);

export const getActiveOpportunities = createSelector(
  getNonZeroOpportunities,
  getMutedOpportunityConfigsBySignalKey,
  (generatedOpportunities, muted) =>
    sortOpportunities(
      generatedOpportunities.filter(opp => opp.signal && !(opp.signal.signalKey in muted)),
      SortProperty.conversionsLost,
      DeprecatedSortOrder.Descending,
    ),
);

export const getMutedOpportunities = createSelector(
  getNonZeroOpportunities,
  getMutedOpportunityConfigsBySignalKey,
  (generatedOpportunities, muted) =>
    sortOpportunities(
      generatedOpportunities.filter(opp => opp.signal && opp.signal.signalKey in muted),
      SortProperty.conversionsLost,
      DeprecatedSortOrder.Descending,
    ),
);

export const getAllMutedConfigs = createSelector(getMutedOpportunityConfigsBySignalKey, muted =>
  Object.values(muted),
);

// use this selector when AppMutedSignals is disabled
export const getOpportunities = createSelector(getNonZeroOpportunities, generatedOpportunities =>
  sortOpportunities(
    generatedOpportunities,
    SortProperty.conversionsLost,
    DeprecatedSortOrder.Descending,
  ),
);

export const opportunityIsStepwise = (opp: Opportunity): boolean =>
  opp.impactScope === ImpactScope.IMPACT_SCOPE_STEPWISE;
export const getOpportunityFromIndex = (opp: Opportunity): number =>
  opportunityIsStepwise(opp)
    ? // TODO (tim): confirm that the impactStats.fromIndex is null on stepwise
      opp.steps.findIndex(d => d.stepStats && d.stepStats.isSignificant)
    : opp.impactStats!.fromIndex;
export const getOpportunityToIndex = (opp: Opportunity): number =>
  opportunityIsStepwise(opp)
    ? // TODO (tim): confirm that the impactStats.fromIndex is null on stepwise
      opp.steps.findIndex(d => d.stepStats && d.stepStats.isSignificant) + 1
    : opp.impactStats!.toIndex;

export const getAffectedUsers = (opp: Opportunity) =>
  int64.toNumber(opp.steps[getOpportunityFromIndex(opp)]?.affectedUsers);

export const getUnaffectedUsers = (opp: Opportunity) =>
  int64.toNumber(opp.steps[getOpportunityFromIndex(opp)]?.unaffectedUsers);

export const getDetailPaginationTotalSteps = createSelector(getOpportunityDetail, detail => {
  const totalSteps: number[] = [];
  if (!detail || detail.impactScope !== ImpactScope.IMPACT_SCOPE_STEPWISE || !detail.steps) {
    return totalSteps;
  }
  detail.steps.forEach((step, i) => {
    if (step.stepStats && step.stepStats.conversionsLost) {
      totalSteps.push(i);
    }
  });
  return totalSteps;
});

export const getConversionSummary = createSelector(
  getOpportunityDetail,
  (detail): ConversionSummaryData[] => {
    let firstAffected: number = 0;
    let firstUnaffected: number = 0;
    // convert to numbers
    if (!detail) return [];
    const firstStep = detail.steps[0];
    if (firstStep) {
      firstAffected = +firstStep.affectedUsers;
      firstUnaffected = +firstStep.unaffectedUsers;
    }

    return detail.steps.map((step, i): ConversionSummaryData => {
      const affectedUsers = +step.affectedUsers || 0;
      const unaffectedUsers = +step.unaffectedUsers || 0;
      // two grouped bars, one for affected and one for unaffected
      const data: Datum[] = [
        {
          data: unaffectedUsers,
          class: 'unaffected',
          percentage: unaffectedUsers / firstUnaffected,
        },
        {
          data: affectedUsers,
          class: 'affected',
          percentage: affectedUsers / firstAffected,
        },
      ];
      return {
        data,
        // ordinal x scales cannot differentiate between identical names,
        // add a numeric prefix to ensure each bar is treated separately
        label: `${i + 1}. ${step.name}`,
      };
    });
  },
);

export const getDetailSignalEvent = createSelector(
  getOpportunityDetail,
  (detail): Event | null => detail?.signal?.event || null,
);

const conversionSummaryFromOpportunity = (opportunity: Opportunity): ConversionSummaryDataV2[] => {
  if (!opportunity) return [];
  let totalUsers: number = 0;
  // convert to numbers
  const firstStep = opportunity.steps[0];
  if (firstStep) {
    totalUsers += +firstStep.affectedUsers;
    totalUsers += +firstStep.unaffectedUsers;
  }
  const { stepwise, simulatedStepwiseLoss } = calculateSimulatedStepwiseLoss(opportunity);

  return opportunity.steps.map((step, i): ConversionSummaryDataV2 => {
    const affectedUsers = +step.affectedUsers || 0;
    const unaffectedUsers = +step.unaffectedUsers || 0;
    const stepTotal = affectedUsers + unaffectedUsers;

    let lostConversions = 0,
      conversionDelta = 0;

    // the stepStats contained on the previous step contain the
    // value for lost conversions / conversion delta.
    const lastStep = opportunity.steps[i - 1];
    if (lastStep && lastStep.stepStats) {
      lostConversions = +lastStep.stepStats.conversionsLost;
      conversionDelta = lastStep.stepStats.conversionDelta;
    }

    const fromStep =
      opportunity.impactStats && opportunity.steps[opportunity.impactStats.fromIndex];
    return {
      id: `${step.name}-${i}`,
      stepTotal,
      stepTotalPercentage: stepTotal / totalUsers,
      affected: affectedUsers,
      affectedConversion:
        lastStep && lastStep.stepStats
          ? lastStep.stepStats.affectedConversion
          : fromStep
          ? affectedUsers / +fromStep.affectedUsers
          : 0,
      unaffected: unaffectedUsers,
      unaffectedConversion:
        lastStep && lastStep.stepStats
          ? lastStep.stepStats.unaffectedConversion
          : fromStep
          ? unaffectedUsers / +fromStep.unaffectedUsers
          : 0,
      lostConversions,
      conversionDelta,
      stepwise,
      simulatedStepwiseLoss: simulatedStepwiseLoss[i] || 0,
      totalConversionsLost: +(opportunity.impactStats?.conversionsLost || 0),
      totalAffectedConversion: opportunity.impactStats?.affectedConversion || 0,
      totalUnaffectedConversion: opportunity.impactStats?.unaffectedConversion || 0,
      // ordinal x scales cannot differentiate between identical names,
      // add a numeric prefix to ensure each bar is treated separately
      label: step.name,
      isSignificant: step.stepStats ? step.stepStats.isSignificant : false,
    };
  });
};

export const getConversionSummaryV2 = createSelector(
  getOpportunityDetail,
  (detail): ConversionSummaryDataV2[] => (detail ? conversionSummaryFromOpportunity(detail) : []),
);

// Helper function to distribute the overall loss across the given steps
// The math matches the overall shape of the conversion funnel
function calculateSimulatedStepwiseLoss(opportunity: Opportunity) {
  let stepwise = false;
  // coefficient multiplied at each step
  const lossCoefficients: number[] = [];
  // loss per step, after adding in the distribution
  // shift all steps over by one, by adding in a default 0
  const simulatedStepwiseLoss: number[] = [0];
  const conversionsLost: number = +(opportunity.impactStats?.conversionsLost || 0);

  opportunity.steps.forEach((step, i) => {
    if (step.stepStats) stepwise = true;

    // if this is a step before the initial step, or after the last step, no loss will be
    // attributed to that step.
    if (
      opportunity.impactStats &&
      (i < opportunity.impactStats.fromIndex || i > opportunity.impactStats.toIndex)
    ) {
      lossCoefficients.push(0);
      return;
    }

    if (i < opportunity.steps.length - 1) {
      const nextStep = opportunity.steps[i + 1];
      if (!nextStep) return;
      const affectedConv = +nextStep.affectedUsers / +step.affectedUsers;
      const unaffectedConv = +nextStep.unaffectedUsers / +step.unaffectedUsers;

      if (affectedConv > unaffectedConv) {
        lossCoefficients.push(0);
        return;
      }

      lossCoefficients.push((unaffectedConv - affectedConv) * +step.affectedUsers);
    }
  });
  const totalLossCoeff = sum(lossCoefficients);
  const lossMultiplier = conversionsLost / totalLossCoeff;
  lossCoefficients.forEach(coef => {
    simulatedStepwiseLoss.push(Math.floor(coef * lossMultiplier));
  });
  const currTotalLoss = sum(simulatedStepwiseLoss);
  // add remaining lost conversions to the first impacted step
  if (currTotalLoss < conversionsLost) {
    if (opportunity.impactStats && !simulatedStepwiseLoss[opportunity.impactStats.fromIndex + 1]) {
      simulatedStepwiseLoss[opportunity.impactStats.fromIndex + 1] = 0;
    }
    if (opportunity.impactStats)
      simulatedStepwiseLoss[opportunity.impactStats.fromIndex + 1] +=
        conversionsLost - currTotalLoss;
  }
  return {
    simulatedStepwiseLoss,
    stepwise,
  };
}

export const getGenerateOpportunitiesStatus = (state: AppState) =>
  state.impacts.generateOpportunitiesStatus;

export const getOpportunityTableLoading = createSelector(
  getGenerateOpportunitiesStatus,
  status =>
    status === OpportunitiesLoadingStatus.fetchingOpportunities ||
    status === OpportunitiesLoadingStatus.fetchingSignals,
);

export const getOpportunitySignalsLoading = (state: AppState) =>
  state.impacts.generatedSignals.loading;

export interface OpportunityErrors {
  error: string;
  className: string;
}

export const getOpportunityErrors = createSelector(
  getOpportunityTableLoading,
  getCurrentFunnel,
  getCurrentSegment,
  getGeneratedOpportunitiesList,
  (isLoading, funnel, segment, opportunities): OpportunityErrors[] => {
    const errors = [];
    const noOpportunities = !opportunities || !opportunities.length;
    if (!isLoading && noOpportunities && !funnel) {
      errors.push({
        error: intl.formatMessage({
          defaultMessage: 'Select a Conversion Funnel to find opportunities',
          id: 'LhqcZE',
        }),
        className: 'error',
      });
    }
    if (!isLoading && noOpportunities && funnel && !segment) {
      errors.push({
        error: intl.formatMessage({
          defaultMessage: 'Select a Segment to find opportunities',
          id: 'WBTGVF',
        }),
        className: 'error',
      });
    }
    return errors;
  },
);

export const getConversionPercentage = createSelector(
  getFunnelMetricsData,
  getConversionPctFromFunnelMetrics,
);

export const getTotalSessionsOrUsers = createSelector(
  getFunnelMetricsData,
  (funnelMetrics): number => {
    const steps = funnelMetrics?.data?.steps;
    if (!steps || !steps.length) return 0;
    return steps[0]?.table[0]?.value ?? 0;
  },
);

const getConfigIds = (state: AppState) => state.impacts.configIds;
const getConfigsById = (state: AppState) => state.impacts.configsById;

// Get a list of all the known configs, including Conversion/Segment
export const getConfigs = createSelector(
  getConfigIds,
  getConfigsById,
  getSavedFunnelsById,
  getSegmentsById,
  (ids, byId, funnelsById, segmentsById): ParsedImpactsTableConfig[] => {
    if (isEmpty(byId) || isEmpty(funnelsById) || isEmpty(segmentsById)) {
      return [];
    }
    return ids
      .map(id => {
        const config = byId[id as string];
        if (!config) return null;
        const funnel = funnelsById[config.funnelId];
        const segment = segmentsById[config.segmentId];
        return {
          ...config,
          ParsedFunnel: funnel ?? null,
          ParsedSegment: segment ?? null,
        };
      })
      .filter(isNonNull)
      .sort((a, b) => a.name.localeCompare(b.name));
  },
);

const getCurrentConfigId = (state: AppState) => state.impacts.currentConfigId;
// Combine ImpactsTableConfig with the live values from the conversion and segment.
export const getCurrentSavedConfig = createSelector(
  getCurrentConfigId,
  getConfigsById,
  getSavedFunnelsById,
  getSegmentsById,
  (id, byId, funnelsById, segmentsById): ParsedImpactsTableConfig | null => {
    if (!id) return null;
    const config = byId[id as string];
    return !config
      ? null
      : {
          ...validateAndAdjustConfig(config),
          ParsedFunnel: funnelsById[config.funnelId] ?? null,
          ParsedSegment: segmentsById[config.segmentId] ?? null,
        };
  },
);

export const makeGetCurrentConfigById = (configId: string) =>
  createSelector(getConfigsById, configsById => configsById[configId]);

// for the current detail view, get the human readable step names.
export const makeGetStepDetailSelector = (
  flags: Flags,
  displayFn: (e: EventType) => string | undefined,
) =>
  createSelector(getOpportunityDetail, getCurrentFunnel, (detail, funnel): string[] => {
    const fallBackStepName = defineMessage({
      defaultMessage: 'Step {stepNum}',
      id: 'RD9t4u',
      description: 'Name for sequential steps a user took. e.g step 1, step 2, etc',
    });
    const events = funnel?.events;
    if (!events) return [];
    if (!detail)
      return (
        events?.events.map((_, i) =>
          getEventTextByStepIndex(
            events,
            i,
            intl.formatMessage(fallBackStepName, { stepNum: i + 1 }),
            flags,
            displayFn,
          ),
        ) ?? []
      );
    return detail.steps.map((_, i) =>
      getEventTextByStepIndex(
        events,
        i,
        intl.formatMessage(fallBackStepName, { stepNum: i + 1 }),
        flags,
        displayFn,
      ),
    );
  });

const hadExperience = (state: AppState) => state.impacts.userSentence.hadExperience;
const converted = (state: AppState) => state.impacts.userSentence.converted;
/**
 * Based on the current impact detail, returns the event search that is needed to provide context for the current
 * conversion for the queries to the matching user searchie.
 *
 * All the cases are documented:
 * https://docs.google.com/document/d/1dkX5ysIWZcm3tw6vz7eSMpGScLcoNITAOPg03Qrr_fQ
 */
export const getSessionSearch = createSelector(
  getConversionParams,
  getDetailSignalEvent,
  hadExperience,
  converted,
  (
    conversionParams,
    signalEvent,
    hadExperience,
    converted,
  ): { eventSearch: EventSearch; markersSegmentDefinition: SegmentDefinition } | undefined => {
    if (!conversionParams || !signalEvent) {
      return undefined;
    }
    const { funnelEventSequence, segmentDefinition, analysisTimeRange, requireSameSession } =
      conversionParams;
    if (!funnelEventSequence || !segmentDefinition || !analysisTimeRange) {
      return undefined;
    }
    return makeSessionSearch(
      signalEvent,
      funnelEventSequence,
      segmentDefinition,
      hadExperience,
      converted,
      requireSameSession,
      analysisTimeRange,
    );
  },
);

function makeSessionSearch(
  signalEvent: Event,
  funnelEventSequence: EventSequence,
  segmentDefinition: SegmentDefinition,
  experiencedInput: boolean,
  converted: boolean,
  inSameSession: boolean,
  timeRange: TimeRange,
): { eventSearch: EventSearch; markersSegmentDefinition: SegmentDefinition } {
  const funnelConstraints: Constraints = {
    inOrder: true,
    inSameSession,
    platform: Platform.PLATFORM_INVALID,
    timeRange,
    completeSessions: true, // always default to true for Conversions INS-8482
    withinTime: funnelEventSequence.constraints?.withinTime ?? Duration.zero,
    eventThresholds: funnelEventSequence.constraints?.eventThresholds ?? [],
  };
  const funnelExcludeConstraints: Constraints = { ...funnelConstraints }; // force in order for certain cases for correct exclusions of users who converted
  const allFunnelBehaviors: Behavior[] = funnelEventSequence.events.map(
    (event): Behavior => ({ event }),
  );
  const firstStepBehavior: Behavior = allFunnelBehaviors[0] ?? {};
  const signalBehavior: Behavior = { event: signalEvent };
  const experiencedInputBehaviors: Behavior[] = [firstStepBehavior, signalBehavior];

  const eventSearch: EventSearch = {
    for: [segmentDefinition], // always includes the target segment definition
    matchingBehaviors: [], // will get set based on case
    select: [], // will get set based on case
  };
  let markersSegmentDefinition: SegmentDefinition;

  if (experiencedInput && !converted) {
    // case 1: experienced the input and did not convert
    eventSearch.matchingBehaviors = [
      {
        behaviors: experiencedInputBehaviors,
        excludeBehaviors: allFunnelBehaviors,
        constraints: funnelConstraints,
        excludeConstraints: funnelExcludeConstraints,
        widenToScope: Scope.SCOPE_EVENT,
      },
    ];
    markersSegmentDefinition = {
      userProperties: [],
      excludeUserProperties: [],
      behaviors: experiencedInputBehaviors,
      excludeBehaviors: [],
      constraints: funnelConstraints,
      excludeConstraints: null,
    };
  } else if (experiencedInput && converted) {
    // case 2: experienced the input and converted
    eventSearch.matchingBehaviors = [
      // match entire funnel
      {
        behaviors: allFunnelBehaviors,
        excludeBehaviors: [],
        constraints: funnelConstraints,
        excludeConstraints: null,
        widenToScope: Scope.SCOPE_EVENT,
      },
      // and the input behaviors
      {
        behaviors: experiencedInputBehaviors,
        excludeBehaviors: [],
        constraints: funnelConstraints,
        excludeConstraints: null,
        widenToScope: Scope.SCOPE_EVENT,
      },
    ];
    // if going the markers route, just match all funnel behaviors and signal events in any order
    markersSegmentDefinition = {
      userProperties: [],
      excludeUserProperties: [],
      behaviors: [...allFunnelBehaviors, signalBehavior],
      excludeBehaviors: [],
      constraints: { ...funnelConstraints, inOrder: false },
      excludeConstraints: null,
    };
  } else if (!experiencedInput && !converted) {
    // case 3: did not experience the input and did not convert
    eventSearch.matchingBehaviors = [
      // match at least first step but exclude folks that completed the entire funnel
      {
        behaviors: [firstStepBehavior],
        excludeBehaviors: allFunnelBehaviors,
        constraints: funnelConstraints,
        excludeConstraints: funnelExcludeConstraints,
        widenToScope: Scope.SCOPE_EVENT,
      },
      // and exclude where the signal was experienced
      {
        behaviors: [],
        excludeBehaviors: experiencedInputBehaviors,
        constraints: funnelConstraints,
        excludeConstraints: funnelExcludeConstraints,
        widenToScope: Scope.SCOPE_SESSION,
      },
    ];
    markersSegmentDefinition = {
      userProperties: [],
      excludeUserProperties: [],
      behaviors: [firstStepBehavior],
      excludeBehaviors: [],
      constraints: funnelConstraints,
      excludeConstraints: null,
    };
  } else {
    // case 4: did not experience the input and converted
    eventSearch.matchingBehaviors = [
      {
        behaviors: allFunnelBehaviors,
        excludeBehaviors: experiencedInputBehaviors,
        constraints: funnelConstraints,
        excludeConstraints: funnelExcludeConstraints,
        widenToScope: Scope.SCOPE_EVENT,
      },
    ];
    markersSegmentDefinition = {
      userProperties: [],
      excludeUserProperties: [],
      behaviors: allFunnelBehaviors,
      excludeBehaviors: [],
      constraints: funnelConstraints,
      excludeConstraints: null,
    };
  }

  return {
    eventSearch,
    markersSegmentDefinition,
  };
}

export const getOpportunitySnapshotsById = (state: AppState) =>
  state.impacts.opportunitySnapshots.byId;
export const getSnapshots = createSelector(getOpportunitySnapshotsById, byId =>
  // sort in descending order by createdAt
  Object.values(byId)
    .slice()
    .sort((a, b) => Timestamp.toMillis(b.createdAt) - Timestamp.toMillis(a.createdAt)),
);
export const opportunitiesFromSnapshots = createSelector(
  getSnapshots,
  (snapshots): OpportunitiesWithID[] => snapshots.map(opportunityFromSnapshot),
);

const getSnapshotId = (state: AppState) => state.impacts.snapshotId;

export const getOpportunitySnapshot = createSelector(
  getOpportunitySnapshotsById,
  getSnapshotId,
  (snapshotsById, id) => {
    if (!snapshotsById || !id) return null;
    return snapshotsById[id];
  },
);

export const getOpportunitySnapshotDetail = createSelector(getOpportunitySnapshot, snapshot =>
  snapshot ? opportunityFromSnapshot(snapshot) : null,
);

export const getSnapshotDetailSteps = createSelector(getOpportunitySnapshot, snapshot =>
  snapshot ? snapshot.steps.map(s => s.name) : [],
);

export const getSnapshotPaginationTotalSteps = createSelector(getOpportunitySnapshot, detail => {
  const totalSteps: number[] = [];
  if (!detail || detail.impactScope !== ImpactScope.IMPACT_SCOPE_STEPWISE || !detail.steps) {
    return totalSteps;
  }
  detail.steps.forEach((step, i) => {
    if (step.stepStats && step.stepStats.conversionsLost) {
      totalSteps.push(i);
    }
  });
  return totalSteps;
});

export const getSnapshotConversionPercentage = createSelector(
  getOpportunitySnapshot,
  (snapshot): number => {
    if (!snapshot) return 0;
    const counts = snapshot.steps.map(s => +s.affectedUsers + +s.unaffectedUsers);
    if (!counts.length || counts.length === 1) return 0;
    const first = counts[0];
    if (first == null || +first === 0) return 0;
    const last = counts[counts.length - 1];
    return (last ? +last : 0) / +first;
  },
);

export const getSnapshotConversionSummary = createSelector(
  getOpportunitySnapshot,
  snapshot => snapshot && conversionSummaryFromOpportunity(opportunityFromSnapshot(snapshot)),
);

export const getSnapshotAnalysisMode = createSelector(getOpportunitySnapshot, snapshot => {
  if (!snapshot) return AnalysisMode.ANALYSIS_MODE_INVALID;
  return snapshot.analysisMode;
});

const getSnapshotInSameSession = createSelector(
  getSnapshotAnalysisMode,
  analysisMode =>
    analysisMode === AnalysisMode.ANALYSIS_MODE_INDV_SAME_SESSION ||
    analysisMode === AnalysisMode.ANALYSIS_MODE_SESSIONS,
);

export const getSnapshotIsSessionMode = createSelector(
  getSnapshotAnalysisMode,
  analysisMode => analysisMode === AnalysisMode.ANALYSIS_MODE_SESSIONS,
);

export const getSnapshotDetailSignalEvent = createSelector(
  getOpportunitySnapshot,
  (snapshot): Event | null => snapshot?.signal?.event || null,
);

export const getSnapshotConversionParams = createSelector(
  getOpportunitySnapshot,
  getSnapshotAnalysisMode,
  getSnapshotInSameSession,
  (snapshot, analysisMode, inSameSession): ConversionParams | null => {
    if (!snapshot || !snapshot?.segmentDefinition || !snapshot?.eventSequence) return null;

    let end = snapshot.timeRange?.endTime;
    const start = snapshot.timeRange?.startTime;
    if (!start) return null;
    // The request should include the full day of the custom date range.
    // If there is no end time, the "Since" option was used. Reconcile this with the snapshot create time
    // Otherwise, a canned date range should have a precise end time.
    if (!end) {
      end = snapshot.createdAt?.toString();
    }
    if (!end) return null;
    if (
      !isNaN(new Date(end).getTime()) &&
      end == resetDateToMidnight(new Date(end)).toISOString()
    ) {
      end = addOneDay(new Date(end)).toISOString();
    }
    const dimensionsRequestTimeRange = {
      startTime: start,
      endTime: end,
    };

    const segmentDefinition: SegmentDefinition = updateSegmentTimeRange(
      snapshot.segmentDefinition,
      dimensionsRequestTimeRange,
    );
    const funnelEventSequence: EventSequence = merge({}, snapshot.eventSequence, {
      constraints: {
        ...snapshot.eventSequence?.constraints,
        timeRange: dimensionsRequestTimeRange,
      },
    });

    return {
      analysisTimeRange: dimensionsRequestTimeRange,
      segmentDefinition,
      funnelEventSequence,
      requireSameSession: inSameSession,
      analysisMode,
    };
  },
);

export const getSessionSearchForSnapshot = createSelector(
  getSnapshotConversionParams,
  getSnapshotDetailSignalEvent,
  hadExperience,
  converted,
  (
    conversionParams,
    signalEvent,
    hadExperience,
    converted,
  ): { eventSearch: EventSearch; markersSegmentDefinition: SegmentDefinition } | undefined => {
    if (!conversionParams || !signalEvent) {
      return undefined;
    }
    const { funnelEventSequence, segmentDefinition, analysisTimeRange, requireSameSession } =
      conversionParams;
    if (!funnelEventSequence || !segmentDefinition || !analysisTimeRange) {
      return undefined;
    }
    return makeSessionSearch(
      signalEvent,
      funnelEventSequence,
      segmentDefinition,
      hadExperience,
      converted,
      requireSameSession,
      analysisTimeRange,
    );
  },
);

export const getSnapshotRevenueEvent = createSelector(
  getOpportunitySnapshot,
  snapshot => snapshot?.revenueEvent,
);

export const getAverageOrderValue = (state: AppState) =>
  state.impacts.revenueAttribution.averageOrderValue.data;

const toStickyTimeRange = (dateRange: DateRange): TimeRange => {
  const todaySearch = moment().subtract(30, 'minute').toISOString();
  const todayMidnight = resetDateToMidnight(new Date()).toISOString();
  let start: string, end: string;
  switch (dateRange.id) {
    case DateRangeID.Today:
      start = todayMidnight;
      end = todaySearch;
      break;
    case DateRangeID.Yesterday:
      start = resetDateToMidnight(moment().subtract(1, 'day').utc().toDate()).toISOString();
      end = todayMidnight;
      break;
    case DateRangeID.Past60Minutes: {
      const startMoment = moment().startOf('minute').subtract(59, 'minute');
      start = startMoment.clone().utc().toDate().toISOString();
      end = startMoment.clone().add(60, 'minute').utc().toDate().toISOString();
      break;
    }
    case DateRangeID.Past3Hours: {
      const startMoment = moment().startOf('hour').subtract(2, 'hour');
      start = startMoment.clone().utc().toDate().toISOString();
      end = startMoment.clone().add(3, 'hour').utc().toDate().toISOString();
      break;
    }
    case DateRangeID.Past6Hours: {
      const startMoment = moment().startOf('hour').subtract(5, 'hour');
      start = startMoment.clone().utc().toDate().toISOString();
      end = startMoment.clone().add(6, 'hour').utc().toDate().toISOString();
      break;
    }
    case DateRangeID.Past24Hours:
      start = moment().subtract(1, 'day').utc().toDate().toISOString();
      end = todaySearch;
      break;
    case DateRangeID.PastWeek:
      start = resetDateToMidnight(moment().subtract(6, 'days').utc().toDate()).toISOString();
      end = todaySearch;
      break;
    case DateRangeID.PastMonth:
      start = resetDateToMidnight(moment().subtract(29, 'days').utc().toDate()).toISOString();
      end = todaySearch;
      break;
    default:
      return {
        startTime: dateRange.range.Start,
        endTime: dateRange.range.End,
      };
  }

  return {
    startTime: start,
    endTime: end,
  };
};

export const getIsRevenueFunnel = createSelector(
  getCurrentFunnel,
  getRevenueEvent,
  (funnel, revenueEvent): boolean =>
    !!funnel && !!revenueEvent && isRevenueFunnel(funnel.events, revenueEvent),
);

export const getCurrentCreateSnapshotRequest = createSelector(
  getOpportunityDetail,
  getAnalysisMode,
  getCurrentSavedConfig,
  getOrgId,
  getCurrentSegment,
  getRevenueEvent,
  getAverageOrderValue,
  getCannedDateRange,
  getIsRevenueFunnel,
  getCurrentParamsForSnapshot,
  (
    detail,
    analysisMode,
    tableConfig,
    orgId,
    segment,
    revenueEvent,
    averageOrderValue,
    cannedDateRange,
    isRevenueFunnel,
    snapshotParams,
  ): CreateOpportunitySnapshotRequest | null => {
    if (!detail || !segment || !cannedDateRange) return null;
    const createSnapshotRequest: CreateOpportunitySnapshotRequest = {
      ...detail,
      orgId,
      analysisMode,
      eventSequence: snapshotParams.eventSequence,
      impactTableConfigName:
        tableConfig?.name ||
        intl.formatMessage({ defaultMessage: 'Unsaved Analysis', id: 'm5k+4I' }),
      segmentDefinition: snapshotParams.segmentDefinition,
      segmentName: segment.segment!.segment!.name,
      funnelName: snapshotParams.name,
      isRevenueFunnel,
      revenueEvent,
      averageOrderValue: averageOrderValue ?? 0,
      timeRange: {
        startTime: cannedDateRange.range.Start,
        endTime: moment(new Date(cannedDateRange.range.End).setMinutes(0, 0, 0)).toISOString(),
      },
    };

    // convert canned date range to a custom date range for the snapshot
    const dateRange = DateRanges.find(
      d => d.range.Start === createSnapshotRequest?.timeRange?.startTime,
    );
    if (!dateRange) {
      return createSnapshotRequest;
    }

    const stickyTimeRange = toStickyTimeRange(dateRange);
    if (stickyTimeRange) {
      createSnapshotRequest.timeRange = stickyTimeRange;
      if (createSnapshotRequest.segmentDefinition?.constraints)
        createSnapshotRequest.segmentDefinition.constraints.timeRange = stickyTimeRange;
      if (createSnapshotRequest.segmentDefinition?.excludeConstraints)
        createSnapshotRequest.segmentDefinition.excludeConstraints.timeRange = stickyTimeRange;
      if (createSnapshotRequest.eventSequence?.constraints)
        createSnapshotRequest.eventSequence.constraints.timeRange = stickyTimeRange;
    }

    return createSnapshotRequest;
  },
);

export const getSnapshotDimensionsRequest = (appState: AppState) =>
  createSelector(
    getOrgId,
    getSnapshotConversionParams,
    getOpportunitySnapshot,
    getCacheOptions,
    (
      orgId,
      conversionParams,
      opportunityDetail,
      cacheOptions,
    ): Omit<ComputeDimensionsRequest, 'now' | 'timezone'> | null => {
      if (!conversionParams) return null;
      return {
        orgId,
        conversionParams,
        signal: opportunityDetail?.signal || null,
        cacheOptions,
        impactStep: int64.fromNumber(opportunityDetail?.impactStats?.fromIndex || 0),
        dimensionTypes: DimensionTypes,
      };
    },
  )(appState);

const getDeletePending = (state: AppState) => state.impacts.deleteSnapshotPending;
const getPendingSnapshotId = (state: AppState) => state.impacts.pendingSnapshot;
export const getDeleteSnapshotPending = createSelector(
  getDeletePending,
  getPendingSnapshotId,
  (pending, snapshotId) => ({ pending, snapshotId }),
);

export const getSnapshotAnalysisSummary = createSelector(getOpportunitySnapshot, snapshot => {
  if (!snapshot) return null;
  return {
    cohort: snapshot ? snapshot.segmentName : 'the segment',
    dateRange: {
      id: DateRangeID.Custom,
      name: DateRangeID.Custom,
      range: {
        Start: snapshot.timeRange?.startTime,
        End: snapshot.timeRange?.endTime || snapshot.createdAt?.toString(),
      },
    } as DateRange,
    numSignals: 0,
  };
});

export const getDaysInSnapshotDateRange = createSelector(
  getOpportunitySnapshot,
  (snapshot): number => {
    if (!snapshot) return 0;
    let end = snapshot.timeRange?.endTime;
    const start = snapshot.timeRange?.startTime;
    if (!end || !start) return 0;
    // If the end time is not midnight, include the entire day for counting.
    if (
      !isNaN(new Date(end).getTime()) &&
      end != resetDateToMidnight(new Date(end)).toISOString()
    ) {
      end = resetDateToMidnight(addOneDay(new Date(end))).toISOString();
    }
    // minimum span of 1 day - if End and Start are the same, then the snapshot date range spans a single day
    return Math.max(moment(end).diff(moment(start), 'days'), 1);
  },
);

export const getSnapshotAnnualMultiplier = createSelector(
  getDaysInSnapshotDateRange,
  (daysInDateRange): number => {
    let multiplier = 0;
    if (daysInDateRange) {
      multiplier = 365 / daysInDateRange;
    }

    return multiplier;
  },
);

export const savedOpportunityHashes = createSelector(getSnapshots, snapshots => {
  const snapshotMap: { [key: string]: string } = {};
  snapshots.forEach(s => {
    const {
      label,
      impactScope,
      signal,
      analysisMode,
      eventSequence,
      segmentDefinition,
      timeRange,
    } = s;
    const stuffToHash = {
      label,
      impactScope,
      analysisMode,
      funnel: eventSequence?.events,
      isRevenueFunnel: s.isRevenueFunnel,
      timeRange,
      signal: {},
      segmentDefinition: {},
    };
    stuffToHash.signal = {
      type: signal?.type,
      event: signal?.event,
    };
    const { constraints, excludeConstraints, ...restOfSegmentDefinition } = segmentDefinition ?? {};
    stuffToHash.segmentDefinition = restOfSegmentDefinition;
    const hash = hashObject(stuffToHash);
    snapshotMap[hash] = s.id;
  });

  return snapshotMap;
});

export const getOpportunityHash = createSelector(
  getOpportunityDetail,
  getCurrentSegment,
  getCurrentFunnel,
  getAnalysisMode,
  getRevenueEvent,
  getCannedDateRange,
  (opportunity, segment, funnel, analysisMode, revenueEvent, dateRange) => {
    if (!opportunity || !segment || !funnel) return undefined;
    const { label, impactScope, signal } = opportunity;
    const isRevenueEvent = !!revenueEvent && isRevenueFunnel(funnel.events, revenueEvent);
    const stuffToHash = {
      label,
      impactScope,
      analysisMode,
      funnel: funnel.events?.events,
      isRevenueFunnel: isRevenueEvent,
      timeRange: dateRange ? toStickyTimeRange(dateRange) : null,
      signal: {},
      segmentDefinition: {},
    };
    stuffToHash.signal = {
      type: signal?.type,
      event: signal?.event,
    };
    const { constraints, excludeConstraints, ...restOfSegmentDefinition } =
      segment.segment!.segment!.segmentDefinition!;
    stuffToHash.segmentDefinition = restOfSegmentDefinition;
    return hashObject(stuffToHash);
  },
);

export const getSavedOpportunityIdMatchedByHash = createSelector(
  savedOpportunityHashes,
  getOpportunityHash,
  (hashesOfSavedOpportunities: { [key: string]: string }, hashOfCurrentOpportunity) => {
    if (!hashOfCurrentOpportunity) return null;
    const hashKey = Object.keys(hashesOfSavedOpportunities).find(
      h => h === hashOfCurrentOpportunity.toString(),
    );
    if (!hashKey) return null;
    return hashesOfSavedOpportunities[hashKey];
  },
);

export const getRevenueEventCurrency = (state: AppState): DisplayCurrencyType =>
  state.impacts.revenueAttribution.revenueEvent.data?.currency ||
  DisplayCurrencyType.CURRENCY_INVALID;
export const getTotalRevenue = (state: AppState) =>
  state.impacts.revenueAttribution.totalRevenue.data;

export const revOppsMetricsRequestBody = (
  orgId: string,
  segment: SegmentDetail | null,
  timeRange: TimeRange | null,
  funnel: Funnel | null,
  revenueEvent: RevenueEvent | null,
  reqType: NumericAggregationMethod,
  requestAnalytics?: RequestAnalyticsAttributes,
  skipCache: boolean = false,
): LegacyMetricsRequestBody | null => {
  if (!orgId || !segment || !revenueEvent || !funnel || !timeRange) return null;
  const primary = revenueEvent.definedEventId
    ? { definedEvent: { withId: { value: revenueEvent.definedEventId } } }
    : { custom: { named: { name: revenueEvent.eventName ?? '' } } };
  const metric: Metric = {
    single: {
      event: {
        primary,
        deps: [],
      },
      aggregation: {
        numeric: {
          property: {
            customEventProperty: revenueEvent.eventProperty,
          },
          aggregation: reqType,
          percentileConfig: null,
        },
      },
      rollingWindow: 0,
      rollingWindowType: Measure.RollingWindowType.ROLLING_WINDOW_TYPE_INVALID,
      rollingWindowInterval: TimeInterval.TIME_INTERVAL_INVALID,
    },
  };

  const segmentDefinition: SegmentDefinition = updateSegmentTimeRange(
    { ...segment.segment!.segment!.segmentDefinition! },
    timeRange,
  );

  const eventSequence: EventSequence = merge({}, funnel.events, {
    constraints: { ...funnel.events?.constraints, timeRange },
  });

  const funnelScope: Scope = funnel.events?.constraints?.inSameSession
    ? Scope.SCOPE_SESSION
    : Scope.SCOPE_INDIVIDUAL;

  const funnelMatchingBehaviors: EventSearch.MatchingBehaviors = {
    excludeBehaviors: [],
    behaviors: eventSequence.events.map((event): Behavior => ({ event })),
    constraints: eventSequence.constraints,
    excludeConstraints: null,
    widenToScope: funnelScope,
  };

  const computation: Computation = {
    metric,
    timeRange,
    timeIntervalRange: null,
    timeInterval: TimeInterval.TIME_INTERVAL_ENTIRE_RANGE,
    segmentDefinitions: [segmentDefinition],
    matchingBehaviors: [funnelMatchingBehaviors],
    dimensionSettings: null,
  };

  return {
    orgId,
    computation,
    cacheOptions: { ...metricsCacheOptionsRefresh, skipCache },
    sampleOptions: null,
    pieConsumptionData: null,
    requestAnalytics: requestAnalytics ?? null,
  };
};

export const makeRevOppsMetricsRequestBody = (
  reqType: NumericAggregationMethod,
  requestAnalytics?: RequestAnalyticsAttributes,
) =>
  createSelector(
    getOrgId,
    getCurrentSegment,
    getAnalysisTimeRange,
    getCurrentFunnel,
    getRevenueEvent,
    (orgId, segment, timeRange, funnel, revenueEvent): LegacyMetricsRequestBody | null =>
      revOppsMetricsRequestBody(
        orgId,
        segment,
        timeRange,
        funnel,
        revenueEvent,
        reqType,
        requestAnalytics,
      ),
  );

export const getIsRevenueSnapshot = createSelector(
  getOpportunitySnapshot,
  (snapshot): boolean => snapshot?.isRevenueFunnel || false,
);
export const getSnapshotAverageOrderValue = createSelector(
  getOpportunitySnapshot,
  (snapshot): number => snapshot?.averageOrderValue || 0,
);
export const getSnapshotRevenueEventCurrency = createSelector(
  getOpportunitySnapshot,
  (snapshot): DisplayCurrencyType =>
    snapshot?.revenueEvent?.currency || DisplayCurrencyType.CURRENCY_INVALID,
);

export const getGenerateOpportunitiesProgress = (state: AppState) =>
  state.impacts.generateOpportunitiesProgress;

export const getDimensions = (state: AppState) => state.impacts.dimensions.list;
export const getDimensionLoadingState = (state: AppState) => state.impacts.dimensions.loading;

/**
 * Returns the list of Dimensions by {@link DimensionTypes} order and the loading state.
 */
export const getSortedDimensionsAndLoadingState = createSelector(
  getDimensions,
  getDimensionLoadingState,
  (dimensions, isLoading) => {
    const sortedDimensions = [...dimensions];
    sortedDimensions.sort(
      (item1, item2) => DimensionTypes.indexOf(item1.type) - DimensionTypes.indexOf(item2.type),
    );

    return {
      dimensions: sortedDimensions,
      isLoading,
    };
  },
);

const getConversionsSpecSignals = (state: AppState) =>
  state.impacts.conversionsSpec.conversionsSpecSignals.data;

export const getMappedSpecObjectsForConversions = createSelector(
  getConversionsSpecSignals,
  allSpecObjectsBySlug,
  (conversionsSpecSignals, specObjectsBySlug) => {
    if (!conversionsSpecSignals) return [];
    return conversionsSpecSignals.objects.flatMap(signal => {
      const specObject = specObjectsBySlug[signal.objectSlug];
      return specObject ? [{ ...signal, specObject }] : [];
    });
  },
);

export const getConversionsSpecSignalsRequestState = (state: AppState) =>
  state.impacts.conversionsSpec.conversionsSpecSignals;

export const getConversionsSpecSignalsLoading = (state: AppState) =>
  state.impacts.conversionsSpec.conversionsSpecSignals.loading;
